import type { PulseChart } from '@shared/chart';
import { PLAYFIELD_HEIGHT, PLAYFIELD_WIDTH } from '@shared/gameTypes';
import { hexToRgba } from './pulseRenderColour';
import { RAIL_THICKNESS, STAR_COUNT } from './pulseRenderConstants';
import type { RenderQuality } from '../renderQuality';

interface Star {
  x: number;
  y: number;
  speed: number;
  size: number;
  alpha: number;
}

/**
 * Owns the parallax star field and the static frame elements (gradient
 * background, beat-pulse radial flash, grid, floor + ceiling rails).
 * Stateful because the star field is animated frame-to-frame; rails +
 * background are stateless conceptually but live here so the entire
 * "scene chrome" pass sits in one class.
 *
 * The background linear gradient is cached per (width, height, palette)
 * combo and the radial bloom + grid + star field are gated by the
 * render-quality tier. On phones the per-frame `createLinearGradient` +
 * 40-line grid stroke pass was a meaningful chunk of frame time.
 */
export class SceneRenderer {
  private stars: Star[] = [];
  private quality: RenderQuality;
  private bgGradient: CanvasGradient | null = null;
  private bgGradientKey = '';
  private starCount: number;

  constructor(quality: RenderQuality) {
    this.quality = quality;
    // Stars are decorative parallax; halve them on low tier so the
    // per-frame loop walks fewer entries (and the screen has fewer
    // moving pixels for the compositor to redraw).
    this.starCount = quality.decorations ? STAR_COUNT : Math.round(STAR_COUNT * 0.4);
    this.initStars();
  }

  private initStars(): void {
    this.stars = [];
    for (let i = 0; i < this.starCount; i++) {
      this.stars.push({
        x: Math.random() * PLAYFIELD_WIDTH,
        y: Math.random() * PLAYFIELD_HEIGHT,
        speed: 20 + Math.random() * 110,
        size: Math.random() < 0.7 ? 1 : 2,
        alpha: 0.2 + Math.random() * 0.6,
      });
    }
  }

  drawBackground(
    ctx: CanvasRenderingContext2D,
    chart: PulseChart,
    w: number,
    h: number,
    beatPulse: number,
  ): void {
    // Cache the linear gradient — its inputs (height + palette) only
    // change on resize / track switch, so allocating a fresh one each
    // frame was pure waste. `createLinearGradient` shows up in
    // mobile profiles as a non-trivial fraction of background time.
    const key = `${h}|${chart.palette.bg}|${chart.palette.bgAccent}`;
    if (!this.bgGradient || this.bgGradientKey !== key) {
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, chart.palette.bg);
      grad.addColorStop(1, chart.palette.bgAccent);
      this.bgGradient = grad;
      this.bgGradientKey = key;
    }
    ctx.fillStyle = this.bgGradient;
    ctx.fillRect(0, 0, w, h);

    // The radial bloom + grid are pure decoration; skip both on low
    // tier. Each is a full-viewport pass, so dropping them together
    // halves the background draw cost without losing any gameplay
    // signal.
    if (!this.quality.decorations) {
      return;
    }

    if (beatPulse > 0.05) {
      const radial = ctx.createRadialGradient(
        w / 2,
        h / 2,
        0,
        w / 2,
        h / 2,
        Math.max(w, h) * 0.7,
      );
      radial.addColorStop(0, hexToRgba(chart.palette.pad, 0.15 * beatPulse));
      radial.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = radial;
      ctx.fillRect(0, 0, w, h);
    }

    ctx.save();
    ctx.globalAlpha = 0.06 + beatPulse * 0.05;
    ctx.strokeStyle = chart.palette.pad;
    ctx.lineWidth = 1;
    const gridStep = 40;
    for (let x = 0; x < w; x += gridStep) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = 0; y < h; y += gridStep) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.restore();
  }

  drawStars(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    dt: number,
    scrollMul: number,
  ): void {
    for (const s of this.stars) {
      // Background parallax tracks the playfield's right→left scroll
      // direction so the world feels coherent: pads come from the right,
      // stars also drift right→left.
      s.x -= s.speed * dt * scrollMul;
      if (s.x < 0) {
        s.x = w + Math.random() * 100;
        s.y = Math.random() * h;
      }
      ctx.fillStyle = `rgba(255, 255, 255, ${s.alpha})`;
      ctx.fillRect(s.x, s.y, s.size, s.size);
    }
  }

  drawRails(
    ctx: CanvasRenderingContext2D,
    chart: PulseChart,
    ceilingY: number,
    floorY: number,
    w: number,
    beatPulse: number,
  ): void {
    ctx.save();
    if (this.quality.chromeShadows) {
      const blur = 12 + beatPulse * 16;
      ctx.shadowColor = chart.palette.pad;
      ctx.shadowBlur = blur;
    }
    ctx.fillStyle = chart.palette.pad;
    ctx.globalAlpha = 0.7 + beatPulse * 0.25;
    ctx.fillRect(0, ceilingY - RAIL_THICKNESS, w, RAIL_THICKNESS);
    ctx.fillRect(0, floorY, w, RAIL_THICKNESS);
    ctx.restore();
  }
}
